// components/booking/BookingStepPayment.js
import React, { useState, useEffect } from 'react';
import { ArrowLeft, Lock, Info, Gift } from 'lucide-react';
import BookingSummary from './BookingSummary';
import StripeProvider from '../payment/StripeProvider';
import StripeCheckoutForm from './StripeCheckoutForm';
import GiftCardRedemption from './GiftCardRedemption';
import { ref, push, set, update, get } from 'firebase/database';
import { database } from '../../firebase';
import useAuth from '../../hooks/useAuth';
import { format } from 'date-fns';
import { updateGiftCardUsage } from '../../services/giftCardService';
import emailService from '../../services/emailService';

const BookingStepPayment = ({ 
  serviceDetails, 
  schedulingDetails, 
  vehicleDetails,
  pricingDetails,
  onBack, 
  onSuccess,
  onChangeService,
  onChangeSchedule,
  onChangeVehicle
}) => {
  const { user } = useAuth(); // Get current authenticated user
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPricingBreakdown, setShowPricingBreakdown] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  
  // Gift card state
  const [appliedGiftCard, setAppliedGiftCard] = useState(null);
  
  // Calculate and return totals for display
  const calculateTotals = () => {
    // Get the appropriate price to use
    let servicePrice;
    
    // If we have pricing details and the vehicle is non-standard/non-compact
    if (pricingDetails && 
        pricingDetails.vehicleSize && 
        pricingDetails.vehicleSize !== 'STANDARD' && 
        pricingDetails.vehicleSize !== 'COMPACT') {
      // Use adjusted price for non-standard vehicles
      servicePrice = serviceDetails.adjustedPrice || serviceDetails.price;
    } else {
      // For standard/compact vehicles or when no vehicle pricing info is available
      // use the original price
      servicePrice = serviceDetails.originalPrice || serviceDetails.price;
    }
    
    const taxRate = 0.0825; // 8.25% sales tax
    const tax = servicePrice * taxRate;
    const total = servicePrice + tax;
    
    // Apply gift card discount if one is applied
    let giftCardAmount = 0;
    let remainingBalance = 0;
    
    if (appliedGiftCard) {
      if (appliedGiftCard.balance >= total) {
        // Gift card covers entire amount
        giftCardAmount = total;
        remainingBalance = appliedGiftCard.balance - total;
      } else {
        // Gift card covers partial amount
        giftCardAmount = appliedGiftCard.balance;
        remainingBalance = 0;
      }
    }
    
    // Calculate final amount
    const finalTotal = Math.max(0, total - giftCardAmount);
    
    return {
      subtotal: servicePrice.toFixed(2),
      tax: tax.toFixed(2),
      total: total.toFixed(2),
      giftCardAmount: giftCardAmount.toFixed(2),
      giftCardRemaining: remainingBalance.toFixed(2),
      finalTotal: finalTotal.toFixed(2)
    };
  };

  // Fetch client secret when component loads or when gift card is applied/removed
  useEffect(() => {
    // Only fetch client secret if there's an amount to pay after gift card
    const totals = calculateTotals();
    if (parseFloat(totals.finalTotal) > 0) {
      fetchClientSecret();
    }
  }, [appliedGiftCard]);
  
  // Function to fetch client secret
  const fetchClientSecret = async () => {
    try {
      setPaymentLoading(true);
      setPaymentError(null);
      
      const totals = calculateTotals();
      const orderData = prepareOrderData();

      console.log("Making payment intent API request with amount:", parseFloat(totals.finalTotal));
      
      const response = await fetch('/api/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          amount: parseFloat(totals.finalTotal), // Use final total after gift card applied
          metadata: orderData
        }),
      });

      if (!response.ok) {
        console.error("Payment intent API error response:", response.status, response.statusText);
        const errorText = await response.text();
        console.error("Error response body:", errorText);
        throw new Error(`Payment API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.clientSecret) {
        console.log("Received client secret from API");
        setClientSecret(data.clientSecret);
      } else {
        console.error("No client secret in response:", data);
        setPaymentError(data.error || 'Unable to set up payment. Please try again.');
      }
    } catch (error) {
      console.error('Network error in fetchClientSecret:', error);
      setPaymentError('Network error. Please check your connection and try again.');
    } finally {
      setPaymentLoading(false);
    }
  };
  
  // Handle gift card application
  const handleApplyGiftCard = (giftCard) => {
    console.log("Applying gift card:", giftCard);
    setAppliedGiftCard(giftCard);
  };
  
  // Handle gift card removal
  const handleRemoveGiftCard = () => {
    console.log("Removing gift card");
    setAppliedGiftCard(null);
  };
  
  // Price breakdown section
  const renderPriceBreakdown = () => {
    // Skip this section if we don't have pricing details
    if (!pricingDetails) return null;
    
    // Only show for non-standard/non-compact vehicles with a price multiplier not equal to 1
    if (pricingDetails.multiplier === 1 || 
        pricingDetails.vehicleSize === 'STANDARD' || 
        pricingDetails.vehicleSize === 'COMPACT') {
      return null;
    }
    
    return (
      <div className="mb-6 border-t border-b border-gray-200 py-4">
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-medium text-gray-700 flex items-center">
            <Info className="h-4 w-4 mr-2 text-blue-500" />
            Price Adjustment Details
          </h4>
          <button
            onClick={() => setShowPricingBreakdown(!showPricingBreakdown)}
            className="text-blue-500 text-sm hover:underline"
          >
            {showPricingBreakdown ? 'Hide' : 'Show'}
          </button>
        </div>
        
        {showPricingBreakdown && (
          <div className="bg-gray-50 p-3 rounded-lg text-sm">
            <div className="grid grid-cols-2 gap-2 mb-1">
              <span className="text-gray-600">Base service price:</span>
              <span className="text-right">${serviceDetails.originalPrice || serviceDetails.price.toFixed(2)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-1">
              <span className="text-gray-600">Vehicle size:</span>
              <span className="text-right">{pricingDetails.vehicleSize}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-1">
              <span className="text-gray-600">Size description:</span>
              <span className="text-right">{pricingDetails.sizeDescription}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-1">
              <span className="text-gray-600">Price multiplier:</span>
              <span className="text-right">{pricingDetails.multiplier}x</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-1 pt-2 border-t border-gray-200 font-medium">
              <span className="text-gray-700">Adjusted price:</span>
              <span className="text-right">${pricingDetails.price.toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Prepare order data for Stripe as strings
  const prepareOrderData = () => {
    const orderData = {
      id: `booking-${Date.now()}`,
      service: serviceDetails.title,
      scheduledDate: schedulingDetails?.date ? schedulingDetails.date.toISOString() : null,
      scheduledTime: schedulingDetails?.time || null,
    };
    
    // Add vehicle information as strings
    if (vehicleDetails?.vehicle) {
      orderData.vehicleMake = vehicleDetails.vehicle.make || '';
      orderData.vehicleModel = vehicleDetails.vehicle.model || '';
      orderData.vehicleYear = vehicleDetails.vehicle.year ? vehicleDetails.vehicle.year.toString() : '';
    }
    
    // Add gift card information if applied
    if (appliedGiftCard) {
      orderData.giftCardCode = appliedGiftCard.code;
      orderData.giftCardAmountApplied = calculateTotals().giftCardAmount;
    }
    
    return orderData;
  };
  
  // Function to save appointment to Firebase after successful payment
  const saveAppointmentToFirebase = async (paymentIntent) => {
    try {
      console.log("Starting to save appointment to Firebase");
      const totals = calculateTotals();
      
      // Create the base appointment data structure
      const appointmentData = {
        service: {
          title: serviceDetails.title,
          price: serviceDetails.price,
          description: serviceDetails.description || ''
        },
        schedule: {
          date: schedulingDetails?.date ? schedulingDetails.date.toISOString() : null,
          time: schedulingDetails?.time || null
        },
        vehicle: vehicleDetails?.vehicle ? {
          make: vehicleDetails.vehicle.make || '',
          model: vehicleDetails.vehicle.model || '',
          year: vehicleDetails.vehicle.year || '',
          color: vehicleDetails.vehicle.color || '',
          location: vehicleDetails.location || '' // Important fix
        } : {},
        payment: {
          total: parseFloat(totals.total),
          subtotal: parseFloat(totals.subtotal),
          tax: parseFloat(totals.tax),
          giftCardAmount: parseFloat(totals.giftCardAmount),
          finalTotal: parseFloat(totals.finalTotal),
          status: 'paid',
          date: new Date().toISOString(),
          method: parseFloat(totals.giftCardAmount) > 0 ? 
            (parseFloat(totals.finalTotal) > 0 ? 'credit_card_and_gift_card' : 'gift_card') : 
            'credit_card',
          last4: paymentIntent?.payment_method_details?.card?.last4 || '****'
        },
        status: 'confirmed',
        createdAt: new Date().toISOString(),
        paymentIntentId: paymentIntent?.id || null
      };
      
      // Add gift card information if one was applied
      if (appliedGiftCard) {
        appointmentData.giftCard = {
          id: appliedGiftCard.id,
          code: appliedGiftCard.code,
          amountApplied: parseFloat(totals.giftCardAmount),
          remainingBalance: parseFloat(totals.giftCardRemaining)
        };
      }
      
      console.log("Appointment data prepared:", JSON.stringify(appointmentData, null, 2));
      
      // Determine if this is a guest or authenticated user
      const isGuest = !user;
      let bookingId;
      
      // Get guest information from localStorage if this is a guest user
      if (isGuest) {
        // Extract guest contact information from storage or vehicle details
        const guestEmail = 
          vehicleDetails?.contactEmail || 
          localStorage.getItem('guestEmail') || 
          '';
        const guestPhone = 
          vehicleDetails?.contactPhone || 
          localStorage.getItem('guestPhone') || 
          '';
        
        // Add guest info to the appointment data
        appointmentData.isGuest = true;
        appointmentData.guestEmail = guestEmail;
        appointmentData.guestPhone = guestPhone;
        
        // Generate a unique booking ID for the guest
        const guestBookingRef = push(ref(database, 'guestBookings'));
        bookingId = guestBookingRef.key;
        
        // Set the complete appointment data
        await set(guestBookingRef, {
          ...appointmentData,
          id: bookingId
        });
        
        console.log("Guest booking saved with ID:", bookingId);
        
        // Save confirmation info to localStorage for retrieval in confirmation page
        localStorage.setItem('lastBookingId', bookingId);
        localStorage.setItem('lastBookingDetails', JSON.stringify({
          id: bookingId,
          service: appointmentData.service.title,
          date: appointmentData.schedule.date,
          time: appointmentData.schedule.time,
          email: guestEmail
        }));
        
        // Create an email lookup record for easy reference by email
        if (guestEmail) {
          const safeEmail = guestEmail.replace(/[.#$[\]]/g, '_');
          await set(ref(database, `guestLookup/byEmail/${safeEmail}/${bookingId}`), {
            bookingId: bookingId,
            date: appointmentData.schedule.date,
            service: appointmentData.service.title
          });
          
          console.log("Guest email lookup record created");
        }
      } else {
        // AUTHENTICATED USER PATH
        console.log("Saving appointment for authenticated user:", user.uid);
        
        // Create a new booking entry with a unique ID
        const newBookingRef = push(ref(database, `users/${user.uid}/bookings`));
        bookingId = newBookingRef.key;
        
        // Set the appointment data
        await set(newBookingRef, {
          ...appointmentData,
          id: bookingId
        });
        
        console.log("Authenticated user booking saved with ID:", bookingId);
      }
      
      // UPDATE GIFT CARD - Works for both guest and authenticated users
      if (appliedGiftCard && parseFloat(totals.giftCardAmount) > 0) {
        try {
          console.log("Updating gift card balance for ID:", appliedGiftCard.id);
          await updateGiftCardBalance(appliedGiftCard.id, parseFloat(totals.giftCardAmount), bookingId);
        } catch (giftCardError) {
          console.error('Error updating gift card:', giftCardError);
          // Continue with the booking even if gift card update fails
        }
      }
      
      // Return the complete booking data with ID
      return {
        id: bookingId,
        ...appointmentData
      };
      
    } catch (error) {
      console.error('Error saving appointment to Firebase:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      return null;
    }
  };
  
  // Separate function for updating gift card balance
  const updateGiftCardBalance = async (giftCardId, amountToDeduct, bookingId) => {
    if (!giftCardId) {
      console.error("No gift card ID provided for update");
      return false;
    }
    
    console.log(`Updating gift card ${giftCardId} - deducting ${amountToDeduct}`);
    
    // Get current gift card data
    const giftCardRef = ref(database, `giftCards/${giftCardId}`);
    const giftCardSnapshot = await get(giftCardRef);
    
    if (!giftCardSnapshot.exists()) {
      console.error("Gift card not found in database:", giftCardId);
      return false;
    }
    
    const currentGiftCard = giftCardSnapshot.val();
    console.log("Current gift card data:", JSON.stringify(currentGiftCard, null, 2));
    
    // Parse numeric values safely
    const currentBalance = parseFloat(currentGiftCard.balance);
    
    // Guard against NaN
    if (isNaN(currentBalance)) {
      console.error("Current gift card balance is not a valid number:", currentGiftCard.balance);
      return false;
    }
    
    // Guard against NaN in amount to deduct
    if (isNaN(amountToDeduct)) {
      console.error("Amount to deduct is not a valid number:", amountToDeduct);
      return false;
    }
    
    // Calculate new balance with precision handling
    const newBalance = Number((Math.max(0, currentBalance - amountToDeduct)).toFixed(2));
    
    console.log(`Updating gift card balance: ${currentBalance} - ${amountToDeduct} = ${newBalance}`);
    
    // Add this transaction to the transactions array
    const transactions = Array.isArray(currentGiftCard.transactions) 
      ? [...currentGiftCard.transactions] 
      : [];
      
    transactions.push({
      type: 'redemption',
      amount: amountToDeduct,
      bookingId: bookingId,
      date: new Date().toISOString()
    });
    
    // Create update data
    const updateData = {
      balance: newBalance,
      status: newBalance > 0 ? 'active' : 'redeemed',
      transactions: transactions,
      lastUpdated: new Date().toISOString()
    };
    
    console.log("Updating gift card with data:", JSON.stringify(updateData, null, 2));
    
    // Update the gift card
    await update(giftCardRef, updateData);
    
    // Verify the update worked
    const verifySnapshot = await get(giftCardRef);
    if (verifySnapshot.exists()) {
      const updatedCard = verifySnapshot.val();
      console.log("Gift card updated successfully. New balance:", updatedCard.balance);
      console.log("Gift card status:", updatedCard.status);
    }
    
    return true;
  };
  
  // Function to handle successful payment
  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      console.log("Payment success handler called with intent:", paymentIntent?.id);
      
      // Save the appointment to Firebase
      const savedAppointment = await saveAppointmentToFirebase(paymentIntent);
      
      if (savedAppointment) {
        console.log("Appointment saved successfully:", savedAppointment.id);
        
        // Send confirmation emails
        try {
          console.log("Sending booking confirmation emails...");
          await emailService.sendAllBookingEmails(savedAppointment);
          console.log("Booking emails sent successfully");
        } catch (emailError) {
          console.error("Error sending booking emails:", emailError);
          // Don't fail the booking if emails fail
        }
        
        // Call the onSuccess callback with the saved appointment data
        onSuccess(savedAppointment);
      } else {
        // Still call onSuccess but with a warning
        console.error("Failed to save appointment");
        alert('Your payment was successful, but there was an issue saving your appointment. Please contact support.');
        onSuccess();
      }
    } catch (error) {
      console.error('Error in payment success handler:', error);
      alert('Payment successful, but there was an error processing your booking.');
      onSuccess();
    }
  };
  
  // Handle full gift card payment
  const handleGiftCardOnlyPayment = async () => {
    if (!appliedGiftCard) {
      setPaymentError('No gift card applied');
      return;
    }
    
    const totals = calculateTotals();
    if (parseFloat(totals.finalTotal) > 0) {
      setPaymentError('Gift card does not cover the full amount');
      return;
    }
    
    setIsProcessing(true);
    
    try {
      console.log("Processing gift card-only payment");
      console.log("Gift card details:", JSON.stringify(appliedGiftCard, null, 2));
      console.log("Payment totals:", JSON.stringify(totals, null, 2));
      
      // Create a mock payment intent for gift card only payments
      const mockPaymentIntent = {
        id: `gift-card-payment-${Date.now()}`,
        status: 'succeeded',
        amount: parseFloat(totals.total) * 100, // In cents
        payment_method_type: 'gift_card',
        payment_method_details: {
          gift_card: {
            code: appliedGiftCard.code
          }
        }
      };
      
      // Double-check that the gift card is still valid and has sufficient balance
      try {
        console.log("Verifying gift card balance before proceeding");
        const giftCardRef = ref(database, `giftCards/${appliedGiftCard.id}`);
        const snapshot = await get(giftCardRef);
        
        if (!snapshot.exists()) {
          throw new Error("Gift card no longer exists in the database");
        }
        
        const currentGiftCard = snapshot.val();
        const currentBalance = parseFloat(currentGiftCard.balance);
        const totalAmount = parseFloat(totals.total);
        
        console.log(`Current balance: ${currentBalance}, Required amount: ${totalAmount}`);
        
        if (currentBalance < totalAmount) {
          throw new Error(`Gift card balance (${currentBalance}) is insufficient for the total (${totalAmount})`);
        }
        
        // Check if gift card is expired
        const expirationDate = new Date(currentGiftCard.expiresAt);
        if (expirationDate < new Date()) {
          throw new Error("Gift card has expired");
        }
        
        // Check if gift card is already redeemed
        if (currentGiftCard.status === 'redeemed') {
          throw new Error("Gift card has already been redeemed");
        }
        
        console.log("Gift card verification successful");
      } catch (verificationError) {
        console.error("Gift card verification failed:", verificationError);
        setPaymentError(`Gift card verification failed: ${verificationError.message}`);
        setIsProcessing(false);
        return;
      }
      
      // Save the appointment
      console.log("Saving appointment with mock payment intent");
      const savedAppointment = await saveAppointmentToFirebase(mockPaymentIntent);
      
      if (savedAppointment) {
        console.log("Appointment saved successfully:", savedAppointment.id);
        
        // Send confirmation emails
        try {
          console.log("Sending booking confirmation emails...");
          await emailService.sendAllBookingEmails(savedAppointment);
          console.log("Booking emails sent successfully");
        } catch (emailError) {
          console.error("Error sending booking emails:", emailError);
          // Don't fail the booking if emails fail
        }
        
        onSuccess(savedAppointment);
      } else {
        console.error("Failed to save appointment");
        alert('There was an issue saving your appointment. Please contact support.');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error processing gift card payment:', error);
      console.error('Error details:', error.message);
      console.error('Error stack:', error.stack);
      setPaymentError('An error occurred while processing your gift card payment.');
      setIsProcessing(false);
    }
  };
  
  const totals = calculateTotals();
  const paymentNeeded = parseFloat(totals.finalTotal) > 0;

  // Render Stripe payment form or loading/error state
  const renderStripePaymentForm = () => {
    // If gift card covers full amount, show gift card only payment option
    if (!paymentNeeded && appliedGiftCard) {
      return (
        <div className="mb-6">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <Gift className="h-5 w-5 text-green-500 mt-0.5 mr-3" />
              <div>
                <p className="text-green-800 font-medium">
                  Your gift card covers the full amount!
                </p>
                <p className="text-green-700 text-sm mt-1">
                  No additional payment is required. After this booking, your gift card will have ${totals.giftCardRemaining} remaining.
                </p>
              </div>
            </div>
          </div>
          
          <button
            onClick={handleGiftCardOnlyPayment}
            className="w-full bg-blue-500 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-600 transition-colors"
            disabled={isProcessing}
          >
            {isProcessing ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Processing...
              </div>
            ) : (
              'Complete Booking with Gift Card'
            )}
          </button>
        </div>
      );
    }
    
    // Regular payment flow for credit card payments or partial gift card
    if (paymentLoading) {
      return (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-600 flex items-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
          <span>Preparing payment form...</span>
        </div>
      );
    }
    
    if (paymentError) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
          <p className="font-medium mb-1">Payment setup error</p>
          <p>{paymentError}</p>
          <button 
            onClick={fetchClientSecret}
            className="mt-3 text-sm text-blue-600 hover:text-blue-800"
          >
            Try again
          </button>
        </div>
      );
    }
    
    if (!clientSecret && paymentNeeded) {
      return (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 flex items-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600 mr-3"></div>
          <span>Initializing payment system...</span>
        </div>
      );
    }
    
    // Only render StripeProvider when we have a clientSecret and payment is needed
    if (paymentNeeded && clientSecret) {
      return (
        <StripeProvider clientSecret={clientSecret}>
          <StripeCheckoutForm 
            onSuccess={handlePaymentSuccess}
            onBack={onBack}
            serviceDetails={serviceDetails}
            schedulingDetails={schedulingDetails}
            vehicleDetails={vehicleDetails}
            customerInfo={null}
            paymentDetails={{
              subtotal: parseFloat(totals.subtotal),
              tax: parseFloat(totals.tax),
              giftCardAmount: parseFloat(totals.giftCardAmount),
              total: parseFloat(totals.finalTotal) // Send final total after gift card
            }}
          />
        </StripeProvider>
      );
    }
    
    return null;
  };
  
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden p-6 mb-6">
      {/* Booking Summary */}
      <BookingSummary 
        serviceDetails={serviceDetails}
        schedulingDetails={schedulingDetails}
        vehicleDetails={vehicleDetails}
        showServiceChange={true}
        showScheduleChange={true}
        showVehicleChange={true}
        onChangeService={onChangeService}
        onChangeSchedule={onChangeSchedule}
        onChangeVehicle={onChangeVehicle}
      />
      
      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
        {/* Payment Form */}
        <div className="lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">Payment Information</h3>
          
          {/* Gift Card Redemption */}
          <GiftCardRedemption 
            onApply={handleApplyGiftCard}
            onRemove={handleRemoveGiftCard}
            appliedGiftCard={appliedGiftCard}
          />
          
          {/* Stripe Payment Form */}
          {renderStripePaymentForm()}
        </div>
        
        {/* Order Summary */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="border-b border-gray-200 pb-4 mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">{serviceDetails.title}</span>
                <span className="font-medium">${totals.subtotal}</span>
              </div>
              
              {/* Vehicle price adjustment explanation */}
              {renderPriceBreakdown()}
              
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Sales Tax (8.25%)</span>
                <span>${totals.tax}</span>
              </div>
            </div>
            
            {/* Gift card application */}
            {appliedGiftCard && parseFloat(totals.giftCardAmount) > 0 && (
              <div className="border-b border-gray-200 pb-4 mb-4">
                <div className="flex justify-between text-green-600">
                  <span>Gift Card ({appliedGiftCard.code.slice(-4)})</span>
                  <span>-${totals.giftCardAmount}</span>
                </div>
                
                {parseFloat(totals.giftCardRemaining) > 0 && (
                  <div className="text-sm text-gray-600 mt-1">
                    <span>Remaining gift card balance: ${totals.giftCardRemaining}</span>
                  </div>
                )}
              </div>
            )}
            
            <div className="flex justify-between font-semibold text-lg">
              <span>{appliedGiftCard && parseFloat(totals.giftCardAmount) > 0 ? 'Balance Due' : 'Total'}</span>
              <span>${totals.finalTotal}</span>
            </div>
          </div>
          
          <div className="text-sm text-gray-500 space-y-2">
            <p className="flex items-start">
              <Lock className="h-4 w-4 mr-2 mt-0.5 text-gray-400" />
              Your payment information is encrypted and secure.
            </p>
            <p>By completing your booking, you agree to our Terms of Service and Privacy Policy.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingStepPayment;